import React, { useState, useEffect, useRef } from 'react';
import { StyleSheet, Text, TextInput, TouchableOpacity, View, SafeAreaView, ScrollView, Image, Modal } from 'react-native';
import { DataStore } from 'aws-amplify';
import { Message, SendPicture, AppUser, UserGroup, Conversation } from '../../models';
import * as ImagePicker from 'expo-image-picker';
import Storage from '@aws-amplify/storage';
import * as CmnUtil from '../components/CmnUtil';
import IconImages from '../components/IconImages';
import { Header } from "../components/Header";
import * as CmnDiaLog from "../components/CmnDiaLog";

class ChatContent {
  id: string = '';
  senderId: string = '';
  senderName: string = ''
  content: string = '';
  sendPhotokey: string = '';
  type: string = '';
  date: string = '';
  isImportant: boolean = false;
  isRead: boolean = false;
}

/** ================================
*  チャット画面
*  ================================*/
const ChatMain = ({ route, navigation }: { route: any, navigation: any; }) => {
  // チャット一覧
  const [chatContentList, setChatContentList] = useState<ChatContent[]>(Array());
  // 入力メッセージ
  const [inputValue, setInputValue] = useState('');
  // メッセージ表示部
  const scrollView = useRef<ScrollView>(null);
  // 引き継ぎパラメータ
  const conversasion: Conversation = route.params.groupConversation.conversation;
  const sender: AppUser = route.params.userInfo;
  const partner: UserGroup = route.params.partner;
  // 日付ラベル
  let lastDayLabel = '';

  // 送信画像の拡大
  const [enlargePhotoKey, setEnlargePhotoKey] = useState<string>("");
  const [photoDialogFlag, setPhotoDialogFlag] = useState<boolean>(false);

  // 削除ダイアログ表示フラグ
  const [delChat, setDelChat] = useState<ChatContent | undefined>(undefined);

  // 送信フラグ
  const [sendType, setSendType] = useState(false);

  useEffect(() => {
    fetchMessage();
  }, [route]);

  useEffect(() => {
    // メッセージの既読処理
    chatContentList.map((item: ChatContent) => {
      if (item.senderId !== sender.id && !item.isRead) {
        updateReadFlg(item);
      }
    });

    // チャット情報の購読
    const message = DataStore.observe(Message).subscribe(
      async (value) => {
        const tmpValue: any = value;
        if (tmpValue.opType == 'INSERT' && tmpValue.element.conversationID == conversasion.id) {
          console.log(tmpValue);
          let content = new ChatContent();
          content.id = tmpValue.element.id;
          content.type = 'message';
          content.content = tmpValue.element.content;
          content.senderId = tmpValue.element.senderID;
          const sender = await DataStore.query(AppUser, content.senderId);
          content.senderName = sender?.username || '';
          content.date = CmnUtil.formatDate(new Date());
          content.isRead = tmpValue.element.isRead == true;
          content.isImportant = tmpValue.element.isImportant == true;
          setChatContentList([...chatContentList, content]);
        }
      }
    );
    const sendPicture = DataStore.observe(SendPicture).subscribe(
      async (value) => {
        const tmpValue: any = value;
        if (tmpValue.opType == 'INSERT' && tmpValue.element.conversationID == conversasion.id) {
          let content = new ChatContent();
          content.id = tmpValue.element.id;
          content.type = 'photo';
          content.senderId = tmpValue.element.senderID;
          const sender = await DataStore.query(AppUser, content.senderId);
          content.senderName = sender?.username || '';
          content.sendPhotokey = tmpValue.element.sendPhotokey;
          content.date = CmnUtil.formatDate(new Date());
          content.isRead = tmpValue.element.isRead == true;
          content.isImportant = false;
          setChatContentList([...chatContentList, content]);
        }
      }
    );

    // 最下部にスクロール
    scrollView?.current?.scrollToEnd({ animated: false });

    return () => {
      message.unsubscribe();
      sendPicture.unsubscribe();
    };
  }, [chatContentList]);

  /**
   * メッセージの取得
   */
  async function fetchMessage() {
    let messageArray: ChatContent[] = [];

    if (conversasion.id) {
      // メッセージの取得
      const message_data = (await DataStore.query(Message)).filter(
        c => c.conversation.id === conversasion.id);
      message_data.map((message) => {
        let content = new ChatContent();
        content.id = message.id;
        content.type = 'message';
        content.content = message.content;
        content.senderId = message.sender.id;
        content.senderName = message.sender.username;
        content.date = message.createdAt!;
        content.isRead = message.isRead == true;
        content.isImportant = message.isImportant == true;
        messageArray.push(content);
      });

      // 送信画像の取得
      const picture_data = (await DataStore.query(SendPicture)).filter(
        c => c.conversation.id === conversasion.id);
      picture_data.map((picture) => {
        let content = new ChatContent();
        content.id = picture.id;
        content.type = 'photo';
        content.senderId = picture.sender.id;
        content.senderName = picture.sender.username;
        content.date = picture.createdAt!;
        content.sendPhotokey = picture.sendPhotokey;
        content.isRead = picture.isRead == true;
        content.isImportant = false;
        messageArray.push(content);
      });

      // メッセージを日付順に並べ替える
      messageArray.sort((a, b) => a.date > b.date ? 1 : -1);
      // メッセージ一覧を設定し、描画
      setChatContentList(messageArray);
    }
  };

  /**
   * メッセージの送信
   */
  async function sendMessage(important: boolean) {
    // メッセージの保存
    await DataStore.save(new Message({
      conversation: conversasion,
      sender: sender,
      content: inputValue,
      isSent: true,
      isRead: false,
      isImportant: important,
    }));

    // テキストエリアのクリア
    setInputValue('');
  };

  /**
   * 既読フラグの更新
   */
  async function updateReadFlg(item: ChatContent) {
    if (item.type === 'message') {
      // メッセージの保存
      const original = await DataStore.query(Message, c => c.id("eq", item.id));
      await DataStore.save(
        Message.copyOf(original[0], updated => {
          updated.isRead = true;
        })
      );
    } else {
      // 送信画像の保存
      const original = await DataStore.query(SendPicture, c => c.id("eq", item.id));
      await DataStore.save(
        SendPicture.copyOf(original[0], updated => {
          updated.isRead = true;
        })
      );
    }
  };

  /**
   * 画像選択
   */
  async function selectImage() {
    // 画像を選択するシステムUIを表示
    let result = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ImagePicker.MediaTypeOptions.Images,
      allowsEditing: true,
      // aspect: [4, 3],
      quality: 1,
      exif: true
    } as ImagePicker.ImagePickerOptions);

    if (!result.cancelled) {
      sendImage(result);
    };
  };

  /**
   * 画像の送信
   */
  const sendImage = async (pickerResult: any) => {
    try {
      // S3アップロード
      const upload_path = sender.id + '/chat/' + conversasion.id + '/';
      const file_name = getFileName();
      const img = await CmnUtil.blobConvert(pickerResult.uri);
      const ext = CmnUtil.getExt(pickerResult.uri);
      if (!CmnUtil.checkExt(ext)) {
        CmnUtil.appAlert('画像以外の送信は出来ません。');
        return;
      }
      const uploadUrl = await Storage.put(upload_path + file_name + '.' + ext, img);

      // DataStore登録
      DataStore.save(new SendPicture({
        conversation: conversasion,
        sender: sender,
        sendPhotokey: uploadUrl.key,
        isSent: true,
        isRead: false,
      }));

    } catch (e) {
      console.error(e);
      CmnUtil.appAlert('画像の送信に失敗しました。');
    }
  };

  /**
   * メッセージ削除
   */
  const deleteMassage = async () => {
    try {
      // 画面更新
      const newList = chatContentList.filter(
        (c) =>
          c.type != delChat!.type ||
          c.id != delChat!.id
      );
      setChatContentList([...newList]);

      if (delChat!.type === 'message') {
        // DataStore削除
        await DataStore.delete(Message, c => c.id('eq', delChat!.id));
      } else {
        // DataStore削除
        await DataStore.delete(SendPicture, c => c.id('eq', delChat!.id));
        // S3削除
        await Storage.remove(delChat!.sendPhotokey);
      }

    } catch (e) {
      console.error(e);
      CmnUtil.appAlert('メッセージの削除に失敗しました。');
    }
    setDelChat(undefined);
  };

  /**
   * 画像ファイル名取得(yyyyMMddHHmmss + 拡張子)
   */
  function getFileName() {
    const current = new Date();
    const yyyy = current.getFullYear();
    const MM = ('0' + (current.getMonth() + 1)).slice(-2);
    const dd = ('0' + current.getDate()).slice(-2);
    const HH = ('0' + current.getHours()).slice(-2);
    const mm = ('0' + current.getMinutes()).slice(-2);
    const ss = ('0' + current.getSeconds()).slice(-2);
    return yyyy + MM + dd + HH + mm + ss;
  };

  return (
    <SafeAreaView style={styles.container}>
      <Header onPress={() => navigation.goBack()}
        goBackText={"＜"}
        headerText={partner.groupName}
        headerTextColor={"#ffffff"}
        headerContainerColor={"#70AD47"} />
      { /* メッセージ表示部 */}
      <View style={styles.messageContainer}>
        <CmnDiaLog.PhotoDialog visible={photoDialogFlag} imageKey={enlargePhotoKey} onClickScreen={() => setPhotoDialogFlag(false)} />
        <ScrollView ref={scrollView}>
          {chatContentList && chatContentList.map((message, index) => {
            let dayLabel = CmnUtil.formatDateAndWeek(new Date(message.date));
            if (lastDayLabel == dayLabel) {
              dayLabel = "";
            } else {
              lastDayLabel = dayLabel;
            }

            return (
              <View key={index}>
                {dayLabel != "" && <View style={styles.dayLabelArea}>
                  <Text style={styles.dayLabel}>{dayLabel}</Text>
                </View>}
                {message.senderId !== sender.id ?
                  message.type === 'message' ?
                    <View style={styles.fromThemBox}>
                      <View style={styles.iconContainer}>
                        <SenderIcon senderId={message.senderId} />
                      </View>
                      <View style={styles.msgContainer}>
                        <Text style={styles.senderName}>{message.senderName}</Text>
                        <View style={[styles.fromThem, message.isImportant ? { backgroundColor: "#fff685" } : { backgroundColor: "#ffffff" }]} >
                          <Text style={message.isImportant ? styles.importantText : styles.chatText}>{message.content}</Text>
                          <View style={[styles.leftArrow, message.isImportant ? { backgroundColor: "#fff685" } : { backgroundColor: "#ffffff" }]} />
                          <View style={styles.leftArrowOverlap} />
                        </View>
                      </View>
                    </View>
                    :
                    <View style={styles.fromThemBox}>
                      <View style={styles.iconContainer}>
                        <SenderIcon senderId={message.senderId} />
                      </View>
                      <View style={styles.msgContainer}>
                        <Text style={styles.senderName}>{message.senderName}</Text>
                        <View style={styles.fromThemPhoto}>
                          <TouchableOpacity onPress={() => { setEnlargePhotoKey(message.sendPhotokey); setPhotoDialogFlag(true); }}>
                            <CmnUtil.S3Image imageKey={message.sendPhotokey} style={styles.chatPhoto} />
                          </TouchableOpacity>
                        </View>
                      </View>
                    </View>
                  :
                  message.type === 'message' ?
                    <TouchableOpacity style={[styles.fromMe, message.isImportant ? { backgroundColor: "#fff685" } : { backgroundColor: "#ffffff" }]} onLongPress={() => { setDelChat(message); }}>
                      <Text style={message.isImportant ? styles.importantText : styles.chatText}>{message.content}</Text>
                      <View style={[styles.rightArrow, message.isImportant ? { backgroundColor: "#fff685" } : { backgroundColor: "#ffffff" }]} />
                      <View style={styles.rightArrowOverlap} />
                    </TouchableOpacity>
                    :
                    <View style={styles.fromMePhoto}>
                      <TouchableOpacity onPress={() => { setEnlargePhotoKey(message.sendPhotokey); setPhotoDialogFlag(true); }}
                        onLongPress={() => { setDelChat(message); }}>
                        <CmnUtil.S3Image imageKey={message.sendPhotokey} style={styles.chatPhoto} />
                      </TouchableOpacity>
                    </View>
                }
              </View>
            );
          })
          }
        </ScrollView>
      </View>

      { /* 操作エリア部 */}
      <View>
        { /* メッセージ */}
        <View style={styles.formContainer}>
          <TouchableOpacity onPress={() => { setInputValue(''); }}>
            <Image style={styles.messageIcon} source={IconImages.icClose} />
          </TouchableOpacity>
          <View style={styles.messageInputBase}>
            <Text style={{ opacity: 0 }}>
              {inputValue};
            </Text>
            <TextInput
              style={styles.messageInput}
              value={inputValue}
              onChangeText={(text) => setInputValue(text)}
              multiline={true}
              placeholder='メッセージを入力してください'
            />
          </View>
          <TouchableOpacity onPress={() => {
            if (inputValue !== '') {
              setSendType(true);
            }
          }}>
            <Image style={styles.messageIcon} source={IconImages.icSend} />
          </TouchableOpacity>
        </View>

        { /* 画像 */}
        <View style={styles.imageContainer}>
          <TouchableOpacity onPress={selectImage}>
            <Image style={styles.pictureIcon} source={IconImages.icGallery} />
          </TouchableOpacity>
          <Text style={styles.iconText}>写真を送信</Text>
        </View>
      </View>

      { /* 削除ダイアログ */}
      <CmnDiaLog.SaveDialog
        cancelButtonName="キャンセル"
        saveButtonName={"削除"}
        message={"削除しますか？"}
        visible={delChat != undefined}
        canceAction={() => { setDelChat(undefined); }}
        saveAction={() => { deleteMassage(); }}
      />

      <SelectImportance
        cancelAction={() => { setSendType(false) }}
        normalAction={() => {
          sendMessage(false);
          setSendType(false);
        }}
        importantAction={() => {
          sendMessage(true);
          setSendType(false);
        }}
        visible={sendType}
      />
    </SafeAreaView>
  );
};

// 送信者アイコン
const SenderIcon = ({ senderId }: { senderId: string }) => {

  const [icon, setIcon] = useState<string | null | undefined>(undefined);

  useEffect(() => {
    (async () => {
      const sender = await DataStore.query(AppUser, senderId);
      setIcon(sender?.icon);
    })();
  }, [senderId]);

  return (
    <CmnUtil.S3Image imageKey={icon} style={styles.userPhoto} defaultSource={IconImages.icUser} />
  );
};

// 重要度選択
const SelectImportance = (props: any) => {
  const {
    cancelAction,
    normalAction,
    importantAction,
    visible,
  } = props;

  return (
    <Modal
      transparent={true}
      visible={visible}
    >
      <View style={dialogStyle.dialogContainer}>
        <View style={dialogStyle.dialogBox}>
          <View style={{ flexDirection: "row", width: "90%", justifyContent: "flex-end" }}>
            <TouchableOpacity onPress={cancelAction} >
              <Image style={dialogStyle.dialogIcon} source={IconImages.icCloseBlack} />
            </TouchableOpacity>
          </View>
          <View style={dialogStyle.dialogTopBox}>
            <Text style={dialogStyle.dialogText}>メッセージを送信します</Text>
          </View>
          <TouchableOpacity
            style={[dialogStyle.dialogButton, { backgroundColor: "#70ad47", borderColor: "#507e32" }]}
            onPress={() => normalAction()}
          >
            <Text style={dialogStyle.dialogButtonText}>送信</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[dialogStyle.dialogButton, { backgroundColor: "#c63710", borderColor: "#9d0d0d" }]}
            onPress={() => importantAction()}
          >
            <Text style={dialogStyle.dialogButtonText}>重要度高で送信</Text>
          </TouchableOpacity>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#70AD47",
  },
  messageContainer: {
    flex: 1,
    backgroundColor: "#E2F0D9",
  },
  formContainer: {
    flexDirection: "row",
    backgroundColor: "#70AD47",
    justifyContent: "space-around",
    alignItems: "center"
  },
  imageContainer: {
    backgroundColor: "#70AD47",
    alignItems: "center",
  },
  messageInputBase: {
    width: "70%",
    minHeight: 25,
    maxHeight: 90,
    margin: 2,
  },
  messageInput: {
    borderRadius: 5,
    backgroundColor: "#ffffff",
    paddingLeft: 10,
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
  },
  messageIcon: {
    width: 30,
    height: 30,
  },
  pictureIcon: {
    marginTop: 10,
    width: 60,
    height: 60,
  },
  iconText: {
    color: "#ffffff"
  },

  /** メッセージ用 */
  chatText: {
    color: "#000000",
    fontSize: 11,
  },
  importantText: {
    color: "#ff0000",
    fontSize: 11,
  },
  dayLabelArea: {
    alignItems: "center",
  },
  dayLabel: {
    backgroundColor: "#888888",
    color: "#ffffff",
    borderRadius: 5,
    paddingHorizontal: 5,
    fontSize: 11,
  },
  fromMe: {
    borderColor: "#70ad47",
    padding: 10,
    marginLeft: "15%",
    marginTop: 5,
    marginRight: "5%",
    marginBottom: 5,
    maxWidth: "80%",
    alignSelf: "flex-end",
    borderRadius: 25,
  },
  fromThemBox: {
    flexDirection: "row",
    padding: 10,
    marginTop: 5,
    marginRight: "15%",
    marginBottom: 5,
  },
  fromThem: {
    borderColor: "#70ad47",
    padding: 10,
    marginLeft: "5%",
    marginTop: 5,
    marginRight: "5%",
    marginBottom: 5,
    maxWidth: "80%",
    alignSelf: "flex-start",
    borderRadius: 25,
  },
  userPhoto: {
    borderRadius: 15,
    width: 30,
    height: 30,
    backgroundColor: "#808080",
    zIndex: 1000,
  },
  chatPhoto: {
    width: 100,
    height: 100
  },
  fromMePhoto: {
    padding: 10,
    marginLeft: "45%",
    marginTop: 5,
    marginRight: "5%",
    marginBottom: 5,
    maxWidth: "50%",
    alignSelf: "flex-end",
  },
  fromThemPhoto: {
    padding: 10,
    marginLeft: "5%",
    marginTop: 5,
    marginRight: "45%",
    marginBottom: 5,
    maxWidth: "50%",
    alignSelf: "flex-start",
  },
  rightArrow: {
    position: "absolute",
    width: 20,
    height: 20,
    right: -10,
    bottom: 0,
    borderBottomLeftRadius: 16,
  },
  rightArrowOverlap: {
    position: "absolute",
    backgroundColor: "#E2F0D9",
    width: 26,
    height: 20,
    right: -26,
    bottom: 0,
    borderBottomLeftRadius: 10,
  },
  leftArrow: {
    position: "absolute",
    width: 20,
    height: 20,
    left: -10,
    top: 0,
    borderTopRightRadius: 16,
  },
  leftArrowOverlap: {
    position: "absolute",
    backgroundColor: "#E2F0D9",
    width: 26,
    height: 20,
    left: -26,
    top: 0,
    borderTopRightRadius: 10,
  },
  iconContainer: {
    paddingTop: 2,
    zIndex: 255,
    elevation: 255
  },
  msgContainer: {
    paddingLeft: 8,
    flex: 1
  },
  senderName: {
    fontSize: 10
  }
});

const dialogStyle = StyleSheet.create({
  dialogContainer: {
    position: "absolute",
    width: "100%",
    height: "100%",
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "rgba(150,150,150,0.6)",
  },
  dialogBox: {
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "#E2F0D9",
    paddingVertical: 15,
    borderColor: "#507E32",
    borderWidth: 2,
    borderStartColor: "solid",
    position: "absolute",
    width: "80%",
  },
  dialogIcon: {
    width: 15,
    height: 15,
  },
  dialogTopBox: {
    flex: 0.6,
    justifyContent: "center",
    alignItems: "center",
  },
  dialogText: {
    fontSize: 18,
  },
  dialogButton: {
    width: 150,
    height: 50,
    marginTop: 15,
    justifyContent: "center",
    alignItems: "center",
    borderWidth: 2,
    borderStartColor: "solid",
  },
  dialogButtonText: {
    color: "#ffffff",
  },
});

export default ChatMain;
